/**
 * PublicTrackingMap — live bus position on the map, reusing the existing
 * Google Maps stack (@vis.gl/react-google-maps, same API key/provider mounted
 * at the app root). No new provider or key. Planned stops are shown as a
 * timeline in the page (not geocoded here) to keep Maps quota low.
 */
import React, { useEffect } from 'react';
import { Map, AdvancedMarker, useMap } from '@vis.gl/react-google-maps';
import { Bus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Pos { lat: number; lng: number; heading?: number | null }

export default function PublicTrackingMap({ position }: { position: Pos | null }) {
  const { t } = useTranslation();
  const map = useMap('tracking-map');

  useEffect(() => {
    if (map && position) {
      map.panTo({ lat: position.lat, lng: position.lng });
    }
  }, [map, position?.lat, position?.lng]);

  const center = position ? { lat: position.lat, lng: position.lng } : { lat: 59.78, lng: 5.5 };

  return (
    <Map
      id="tracking-map"
      style={{ width: '100%', height: '100%', minHeight: 320 }}
      defaultCenter={center}
      defaultZoom={position ? 13 : 8}
      disableDefaultUI={false}
      zoomControl={true}
      streetViewControl={false}
      mapTypeControl={false}
      fullscreenControl={false}
      gestureHandling="greedy"
      mapId={process.env.REACT_APP_GOOGLE_MAP_ID || 'DEMO_MAP_ID'}
      data-testid="tracking-map"
    >
      {position && (
        <AdvancedMarker position={{ lat: position.lat, lng: position.lng }} title={t('publicTrackingMap.buss', 'Buss')}>
          <div
            className="flex items-center justify-center w-9 h-9 rounded-full bg-emerald-600 border-2 border-white shadow-lg"
            style={{ transform: position.heading != null ? `rotate(${position.heading}deg)` : undefined }}
          >
            <Bus className="w-5 h-5 text-white" />
          </div>
        </AdvancedMarker>
      )}
    </Map>
  );
}
